import { useState } from "react";
import { useNavigate, Navigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { authTenantServiceApi, iamApi, errorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import BrandLogo from "@/components/BrandLogo";
import { Loader2, ShieldCheck, Layers, Users2, CheckCircle2 } from "lucide-react";

// Mirrors the platform credential policy (auth-service's recovery/complete
// schema: z.string().min(12)). Enforced client-side for a friendly inline
// message; the backend enforces it authoritatively too.
const MIN_PASSWORD_LENGTH = 12;

/**
 * Account activation — reached from the emailed signup link
 * (TENANT_APP_BASE_URL/activate?token=...&email=...). This is the final,
 * self-service step of tenant signup: the owner the provisioning saga
 * created sets their own password here and is signed straight in, with no
 * internal/manual bootstrap.
 *
 * The one-time activation proof (`token`) and the owner's `email` come from
 * the link. We resolve which Auth-platform tenant the email belongs to
 * (tis-auth-tenant-service POST /tenants/resolve), then complete activation
 * via iam's PUBLIC activate-complete endpoint (POST
 * /api/v1/users/activate-complete), scoped to that tenant. That single call
 * validates+consumes the activation token, marks the owner ACTIVE, and sets
 * the INITIAL password server-side (iam to auth-service, backend-to-backend
 * — the browser sends no service secret). On success we log the owner in
 * with the password they just chose and send them to the admin console.
 *
 * This replaces the earlier /auth/recovery/complete approach, which
 * structurally cannot serve a first-time invitee: recovery/complete only
 * UPDATEs an existing password row and refuses non-ACTIVE users. The Hub SPA
 * Activate page uses the same new endpoint.
 */
async function resolveTenantId(email) {
  // POST (email in body, not query) with a GET fallback for the rollout
  // window — identical to AuthContext.resolveTenantByEmail.
  try {
    const res = await authTenantServiceApi.post("/api/v1/tenants/resolve", { email });
    return res?.data?.tenantId ?? null;
  } catch (err) {
    const data = err?.response?.data;
    const routeMissing = err?.response?.status === 404 && data && !data.code;
    if (routeMissing) {
      const res = await authTenantServiceApi
        .get("/api/v1/tenants/resolve", { params: { email } })
        .catch(() => null);
      return res?.data?.tenantId ?? null;
    }
    return null;
  }
}

export default function Activate() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user, login, isPlatformOperator } = useAuth();

  const token = params.get("token") || "";
  const email = params.get("email") || "";
  const linkValid = Boolean(token && email);

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (user) return <Navigate to={isPlatformOperator() ? "/platform" : "/admin"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    setLoading(true);
    try {
      const tenantId = await resolveTenantId(email);
      if (!tenantId) {
        setError("This activation link is invalid or has expired.");
        return;
      }
      // First-time activation: validate+consume the activation token, mark the
      // owner ACTIVE, and set the INITIAL password — one call to iam's PUBLIC
      // activate-complete endpoint (NOT /auth/recovery/complete, which only
      // updates an existing password row and refuses non-ACTIVE users, so it
      // cannot serve a first-time invitee). Browser sends only
      // { activationToken, email, password } + the tenant header; no service
      // secret. iam sets the password in auth-service backend-to-backend.
      await iamApi.post(
        "/users/activate-complete",
        { activationToken: token, email, password },
        { headers: { "x-tis-tenant-id": tenantId } },
      );
      // Sign straight in with the chosen password — completing signup end
      // to end — and land on the admin console.
      await login(email, password);
      navigate("/admin");
    } catch (err) {
      setError(errorMessage(err) || "This activation link is invalid or has expired.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-background">
      {/* Brand / trust panel — hidden on small screens, matches Login */}
      <aside
        className="relative hidden w-1/2 flex-col justify-between overflow-hidden p-12 text-white lg:flex"
        style={{ background: "linear-gradient(150deg, hsl(var(--primary)) 0%, hsl(var(--primary)) 55%, hsl(var(--accent)) 160%)" }}
        aria-hidden="true"
      >
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #fff 0, transparent 40%), radial-gradient(circle at 80% 60%, #fff 0, transparent 35%)" }} />
        <div className="relative rounded-lg bg-white/95 px-4 py-3 shadow-sm w-fit">
          <BrandLogo size="md" />
        </div>
        <div className="relative max-w-md">
          <h2 className="font-heading text-3xl font-bold leading-tight">You're almost in.</h2>
          <p className="mt-3 text-white/80">
            Set a password to finish activating your company account and sign in for the first time.
          </p>
          <ul className="mt-8 space-y-4">
            {[
              { icon: Layers, t: "Multi-portal by design", s: "Talent & Hub share one branded identity." },
              { icon: Users2, t: "Role-based access", s: "Per-portal entitlements via the central Auth platform." },
              { icon: ShieldCheck, t: "Enterprise security", s: "Tenant-isolated data, audited and SSO-ready." },
            ].map(({ icon: Icon, t, s }) => (
              <li key={t} className="flex items-start gap-3">
                <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/15">
                  <Icon className="h-4.5 w-4.5" />
                </span>
                <span>
                  <span className="block font-semibold">{t}</span>
                  <span className="block text-sm text-white/70">{s}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
        <p className="relative text-xs text-white/50">© {new Date().getFullYear()} TIS. All rights reserved.</p>
      </aside>

      {/* Form pane */}
      <main className="flex w-full flex-col items-center justify-center px-6 py-10 lg:w-1/2">
        <div className="w-full max-w-sm">
          <div className="mb-8 flex justify-center lg:hidden">
            <BrandLogo size="lg" />
          </div>

          {!linkValid ? (
            <div data-testid="activate-invalid-link" className="text-center">
              <h1 className="font-heading text-2xl font-bold text-foreground">Invalid activation link</h1>
              <p className="mt-2 text-sm text-muted-foreground">
                This link is missing information or malformed. Please use the link from your activation email, or sign in if you've already set a password.
              </p>
              <a href="/login" data-testid="activate-go-login" className="mt-6 inline-block text-sm font-semibold text-primary hover:underline">
                Go to sign in
              </a>
            </div>
          ) : (
            <>
              <div className="mb-2 flex items-center gap-2 text-primary">
                <CheckCircle2 className="h-5 w-5" />
                <span className="text-xs font-semibold uppercase tracking-wider">Activate your account</span>
              </div>
              <h1 className="font-heading text-2xl font-bold text-foreground">Set your password</h1>
              <p className="mt-1 text-sm text-muted-foreground">
                for <span className="font-medium text-foreground">{email}</span>
              </p>

              <form onSubmit={submit} className="mt-8 space-y-5" noValidate>
                <div className="space-y-1.5">
                  <Label htmlFor="password">New password</Label>
                  <PasswordInput
                    id="password"
                    autoComplete="new-password"
                    placeholder="At least 12 characters"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    data-testid="activate-password-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="confirm">Confirm password</Label>
                  <PasswordInput
                    id="confirm"
                    autoComplete="new-password"
                    placeholder="Re-enter your password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    required
                    data-testid="activate-confirm-input"
                  />
                </div>
                {error && (
                  <div data-testid="activate-error" role="alert" className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                    {error}
                  </div>
                )}
                <Button type="submit" disabled={loading} className="w-full" data-testid="activate-submit-btn">
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Activate &amp; sign in
                </Button>
              </form>

              <p className="mt-6 text-center text-sm text-muted-foreground">
                Already activated?{" "}
                <a href="/login" className="font-medium text-primary hover:underline">Sign in</a>
              </p>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
